/**
 * Pure utility functions for AdminTenantDetailPage (Step 7.2 Phase 1).
 * NO React, NO hooks, NO JSX, NO constants of any kind.
 * All functions extracted byte-for-byte from AdminTenantDetailPage.js.
 */
import { tenantDisplayName } from "../../../utils/tenantDisplayName";
import { formatAuditLog, auditActorDisplay } from "../../../utils/auditDisplay";
import {
  getTodayIsoForOccupancy,
  parseIsoDate,
} from "../../../utils/unitOccupancyStatus";
import { normalizeRevenueFrequency } from "../../../utils/tenancyRevenueBreakdown";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";

export function dateOnlyOrNull(value) {
  const s = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  return s.slice(0, 10);
}

/**
 * The physical move-out as the full edit form ("Bearbeiten") sends it.
 *
 * KND-2 split this out of the former cancellation quad. The three TERMINATION
 * fields — `notice_given_at`, `termination_effective_date`, `terminated_by` —
 * are owned by the termination block in the Mietvertrag card now (discovery
 * E2) and are never part of this PATCH: a key that is not sent cannot
 * overwrite what that block saved (`exclude_unset=True`). What stays is the
 * return date, with the behaviour it always had: a CLOSED "Rückgabe" section
 * sends explicit null (the admin hid it to take the date back), an open one
 * sends the normalized value.
 */
export function buildActualMoveOutPatchFields(open, actualMoveOut) {
  return {
    actual_move_out_date: open ? dateOnlyOrNull(actualMoveOut) : null,
  };
}

/** Mirrors backend scheduling_end_date_from_parts (tenancy_lifecycle). */
export function maxIsoDate(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return a >= b ? a : b;
}

export async function parseAdminErrorFromResponse(res) {
  const text = await res.text();
  try {
    const j = JSON.parse(text);
    if (Array.isArray(j.detail)) {
      return j.detail
        .map((d) => (typeof d === "string" ? d : d.msg || d.message || ""))
        .filter(Boolean)
        .join(" ");
    }
    if (typeof j.detail === "string") return j.detail;
  } catch (_) {
    /* ignore */
  }
  return text || "Die Anfrage ist fehlgeschlagen.";
}

export function formatTenantDocumentDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function formatTenantDocumentType(doc) {
  const mime = String(doc.mime_type || "").toLowerCase();
  const name = String(doc.file_name || "");
  const ext = name.includes(".")
    ? (name.split(".").pop() || "").toLowerCase()
    : "";

  if (mime.includes("pdf") || ext === "pdf") return "PDF";
  if (mime.includes("jpeg") || mime.includes("jpg") || ext === "jpg" || ext === "jpeg") return "JPG";
  if (mime.includes("png") || ext === "png") return "PNG";
  if (
    mime.includes("wordprocessingml") ||
    mime.includes("msword") ||
    ext === "docx" ||
    ext === "doc"
  ) {
    return "DOCX";
  }
  if (ext && /^[a-z0-9]+$/i.test(ext)) return ext.toUpperCase();
  return "Datei";
}

export function formatTenantAuditDateDe(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}.${m}.${y}`;
  }
  return String(iso);
}

export function formatTenantAuditChf(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (Number.isNaN(x)) return String(n);
  return `${x.toLocaleString("de-CH")} CHF`;
}

export function auditLogToTenantHistoryEvent(log) {
  const nv = log.new_values && typeof log.new_values === "object" ? log.new_values : {};
  const ov = log.old_values && typeof log.old_values === "object" ? log.old_values : {};
  const author = auditActorDisplay(log) || "—";
  const { summary, changes } = formatAuditLog(log, { entityType: "tenant" });
  let action_type = "audit_tenant";
  if (nv.document_uploaded != null && String(nv.document_uploaded).trim() !== "") {
    action_type = "audit_document";
  } else if (ov.document_deleted != null && String(ov.document_deleted).trim() !== "") {
    action_type = "audit_document";
  } else if (nv.tenancy || ov.tenancy) {
    action_type = "audit_tenancy";
  } else if (nv.tenancy_revenue || ov.tenancy_revenue) {
    action_type = "audit_revenue";
  }
  return {
    id: `audit-${log.id}`,
    summary,
    audit_changes: changes,
    audit_action: log.action,
    created_at: log.created_at,
    author_name: author,
    action_type,
  };
}

export function formatInvoiceAmount(amount, currency) {
  const cur = currency || "CHF";
  const n = Number(amount);
  if (Number.isNaN(n)) return `${cur} —`;
  const num = n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cur} ${num}`;
}

export function snapIntToAllowedList(raw, allowed, fallback) {
  const v = parseInt(raw, 10);
  if (Number.isFinite(v) && allowed.includes(v)) return v;
  if (!Number.isFinite(v)) return fallback;
  let best = allowed[0];
  let bestDist = Infinity;
  for (const a of allowed) {
    const d = Math.abs(a - v);
    if (d < bestDist) {
      bestDist = d;
      best = a;
    }
  }
  return best;
}

export function tenancyLifecycleStatusLabelDe(key) {
  const k = String(key || "").toLowerCase();
  if (k === "secure") return "Geschützt (Mindestdauer)";
  if (k === "cancellable") return "Kündbar";
  if (k === "notice_given") return "Kündigung ausgesprochen";
  if (k === "always_cancellable") return "Jederzeit kündbar";
  if (k === "free_planned") return "Beendet / frei";
  if (k === "reserved") return "Reserviert";
  return "—";
}

export function formatNoticePeriodMonthsDe(n) {
  const v = parseInt(n, 10);
  if (!Number.isFinite(v)) return "—";
  if (v === 0) return "Sofort";
  if (v === 1) return "1 Monat";
  return `${v} Monate`;
}

export function formatMinimumDurationMonthsDe(n) {
  const v = parseInt(n, 10);
  if (!Number.isFinite(v)) return "—";
  if (v === 0) return "Keine Mindestdauer";
  if (v === 1) return "1 Monat";
  return `${v} Monate`;
}

/** Canonical Mietende for UI: API display_end_date (matches tenancy_lifecycle.tenancy_display_end_date), then synced move_out. */
export function tenancyDisplayEndIso(tn) {
  if (!tn) return null;
  const de = dateOnlyOrNull(tn.display_end_date);
  if (de) return de;
  return dateOnlyOrNull(tn.move_out_date);
}

export function tenancyDisplayStatusLabelDe(ds) {
  const k = String(ds || "").toLowerCase();
  if (k === "active") return "Aktiv";
  if (k === "reserved") return "Reserviert";
  if (k === "notice_given") return "Gekündigt";
  if (k === "ended") return "Beendet";
  if (k === "cancelled") return "Storniert";
  return "—";
}

/**
 * SINGLE SOURCE OF TRUTH for tenancy lifecycle badges (admin UI).
 *
 * Fixed-term vs notice: a `move_out_date` (Vertragsende / Befristet bis) NEVER
 * means "gekündigt". Only a real cancellation signal does:
 * `termination_effective_date` ("Kündigung wirksam per") or `notice_given_at`,
 * or stored `status === "cancelled"`. This mirrors the backend derivation
 * (tenancy_lifecycle.tenancy_derived_display_status), which keys notice_given
 * on `termination_effective_date` — and additionally honours `notice_given_at`.
 *
 * "today" comes from getTodayIsoForOccupancy() (the same source as
 * deriveTenancyLifecyclePreviewForAssign), compared as YYYY-MM-DD strings.
 *
 * @returns {{ key: string, label: string, secondaryLabel: string|null, tone: string }}
 *   key/tone ∈ active | reserved | notice_given | ended | cancelled
 *   secondaryLabel === "Befristet" when a future move_out_date is present and
 *   the tenancy is not cancelled/terminated/ended.
 */
export function getTenancyLifecycleMeta(tenancy, todayIso = getTodayIsoForOccupancy()) {
  const t = tenancy || {};
  const today = String(todayIso || "").slice(0, 10);
  const status = String(t.status || "").toLowerCase();
  const ds = String(t.display_status || "").toLowerCase();
  const mi = dateOnlyOrNull(t.move_in_date);
  const mo = dateOnlyOrNull(t.move_out_date);
  const te = dateOnlyOrNull(t.termination_effective_date);
  const nga = dateOnlyOrNull(t.notice_given_at);
  const act = dateOnlyOrNull(t.actual_move_out_date);
  // move_out_date is the fixed-term end and is NOT a cancellation signal.
  const cancellationSignal = Boolean(te || nga);

  // 1. Storniert (terminal, highest priority)
  if (status === "cancelled" || ds === "cancelled") {
    return { key: "cancelled", label: "Storniert", secondaryLabel: null, tone: "cancelled" };
  }
  // 2. Already physically moved out (actual move-out in the past)
  if (act && act < today) {
    return { key: "ended", label: "Beendet", secondaryLabel: null, tone: "ended" };
  }
  // 3. Real notice / termination (independent of the fixed-term end)
  if (cancellationSignal) {
    return { key: "notice_given", label: "Gekündigt", secondaryLabel: null, tone: "notice_given" };
  }
  // 4. Move-in still in the future → Geplant (· Befristet if a future end exists)
  if (mi && mi > today) {
    const fixed = Boolean(mo && mo > today);
    return {
      key: "reserved",
      label: "Geplant",
      secondaryLabel: fixed ? "Befristet" : null,
      tone: "reserved",
    };
  }
  // 5. Fixed-term end already passed, no notice → Beendet (Befristung abgelaufen)
  if (mo && mo < today) {
    return { key: "ended", label: "Beendet", secondaryLabel: null, tone: "ended" };
  }
  // 6. Active with a future fixed-term end → Aktiv · Befristet
  if (mo && mo >= today) {
    return { key: "active", label: "Aktiv", secondaryLabel: "Befristet", tone: "active" };
  }
  // 7. Terminal stored "ended" with no dates, else open-ended active
  if (status === "ended" || ds === "ended") {
    return { key: "ended", label: "Beendet", secondaryLabel: null, tone: "ended" };
  }
  return { key: "active", label: "Aktiv", secondaryLabel: null, tone: "active" };
}

export function tenancyDraftDisplayEndIso(actualRaw, terminationRaw) {
  return dateOnlyOrNull(actualRaw) || dateOnlyOrNull(terminationRaw) || "";
}

export function deriveTenancyLifecyclePreviewForAssign(
  moveInRaw,
  terminationEffectiveRaw,
  actualMoveOutRaw,
  todayIso = getTodayIsoForOccupancy()
) {
  const today = String(todayIso || "").slice(0, 10);
  const mi = moveInRaw != null ? String(moveInRaw).slice(0, 10) : "";
  const act = dateOnlyOrNull(actualMoveOutRaw);
  const te = dateOnlyOrNull(terminationEffectiveRaw);
  if (act && act < today) return "ended";
  if (te && te >= today) return "notice_given";
  if (te && te < today && (!act || act >= today)) return "notice_given";
  if (mi && mi > today) return "reserved";
  if (mi) return "active";
  return "active";
}

export function storedTenancyStatusForApi(displayKey) {
  const k = String(displayKey || "").toLowerCase();
  if (k === "ended") return "ended";
  if (k === "reserved") return "reserved";
  return "active";
}

/** Subtle note from API display end + display_status only (no extra lifecycle rules). */
export function tenancyEndUrgencyNote(tn, todayIso = getTodayIsoForOccupancy()) {
  const endIso = parseIsoDate(tn?.display_end_date) || parseIsoDate(tn?.move_out_date);
  if (!endIso) return null;
  const ds = String(tn?.display_status || "").toLowerCase();
  if (ds === "ended" || endIso < todayIso) return "bereits beendet";
  if (endIso >= todayIso) {
    const d0 = new Date(`${todayIso}T12:00:00`);
    const d1 = new Date(`${endIso}T12:00:00`);
    const days = Math.round((d1.getTime() - d0.getTime()) / 86400000);
    if (days === 0) return "endet heute";
    if (days > 0) return `endet in ${days} Tag${days === 1 ? "" : "en"}`;
  }
  return null;
}

export function formatChfRent(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return "CHF —";
  return `CHF ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function inspectionCheckStatusLabelDe(s) {
  const k = String(s || "").toLowerCase();
  if (k === "ok") return "OK";
  if (k === "damaged") return "Beschädigt";
  if (k === "missing") return "Fehlt";
  if (k === "not_checked") return "Nicht geprüft";
  if (k === "not_applicable") return "Nicht relevant";
  return s || "—";
}

export function inspectionCheckStatusTextClass(s) {
  const k = String(s || "").toLowerCase();
  if (k === "ok") return "text-emerald-400";
  if (k === "damaged") return "text-orange-400";
  if (k === "missing") return "text-red-400";
  if (k === "not_applicable") return "text-sky-400";
  return "text-gray-500";
}

export function summarizeMoveInInspection(detail) {
  if (!detail?.items?.length) return "Übergabe: —";
  const open = detail.items.some((i) => String(i.check_status || "").toLowerCase() === "not_checked");
  return open ? "Übergabe: offen" : "Übergabe: erledigt";
}

export function summarizeMoveOutInspection(detail) {
  if (!detail?.items?.length) return "Abnahme: —";
  const dm = detail.items.filter((i) => {
    const x = String(i.check_status || "").toLowerCase();
    return x === "damaged" || x === "missing";
  }).length;
  if (dm > 0) return `Abnahme: ${dm} Schäden`;
  const open = detail.items.some((i) => String(i.check_status || "").toLowerCase() === "not_checked");
  return open ? "Abnahme: offen" : "Abnahme: erledigt";
}

export function parseOptionalTenantDepositFloat(value) {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function parseRevenueAmount(value) {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

export function schedulingEndDateFromPartsJs(moveOutRaw, termRaw, actualRaw) {
  const mo = dateOnlyOrNull(moveOutRaw);
  const te = dateOnlyOrNull(termRaw);
  const act = dateOnlyOrNull(actualRaw);
  const contract = maxIsoDate(te, mo);
  if (act) {
    return maxIsoDate(contract, act);
  }
  return contract;
}

export function tenancySchedulingEndIso(t) {
  return schedulingEndDateFromPartsJs(
    t?.move_out_date,
    t?.termination_effective_date,
    t?.actual_move_out_date
  );
}

function isoDatePlusOneDay(iso) {
  const d = dateOnlyOrNull(iso);
  if (!d) return null;
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7)) - 1;
  const day = Number(d.slice(8, 10));
  const dt = new Date(Date.UTC(y, m, day));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

/**
 * Exclusive occupancy end (first free day) for a tenancy — the JS twin of the
 * backend effective_occupancy_excl_end (tenancy_lifecycle). Slice A: move_out_date
 * is now the SINGLE inclusive effective end (the backend already folded
 * termination/actual into it), so the first free day is simply move_out_date + 1.
 * Returns null for an open-ended tenancy (caller treats as infinity).
 *
 * The old (move_out, termination, actual) COALESCE-priority signature is GONE on
 * purpose: keeping termination/actual here is exactly how the room used to be
 * released one day too early on every termination (a same-day handover, forbidden
 * under FAHN inclusive end dates). One-arg makes that regression impossible to
 * reintroduce. (Follow-up: delete this client-side precheck entirely and let the
 * server decide — it drifted once already.)
 */
export function effectiveOccupancyExclEndIso(moveOutRaw) {
  const mo = dateOnlyOrNull(moveOutRaw);
  if (mo) return isoDatePlusOneDay(mo);
  return null;
}

/** UX-only pre-filter for the assign button — NOT authoritative. The server precheck
 *  (overlaps() in tenancy_admin_service) is the source of truth and returns a clean
 *  HTTP 400 on a real overlap. Both sides use move_out_date + 1 (FAHN inclusive end
 *  dates → NO same-day handover). */
export function roomOverlapsAssignProposal(room, unitTenancies, newMoveInRaw, newMoveOutRaw) {
  const rid = String(room?.id ?? room?.room_id ?? "").trim();
  const ourStart = dateOnlyOrNull(newMoveInRaw);
  if (!rid || !ourStart) return false;
  // Proposed EXCLUSIVE end = the raw planned move_out_date + 1 (open-ended → infinity).
  // The backend re-derives the proposed end from termination_effective_date /
  // actual_move_out_date (derive_effective_move_out_date). This hint uses ONLY the raw
  // planned move_out, so when termination/actual would change the derived end it can be
  // MORE PERMISSIVE than the server (i.e. miss an overlap). That is accepted: the server
  // precheck is authoritative. We intentionally do NOT duplicate the backend 3-argument
  // derivation in JavaScript (it drifted once already).
  // Follow-up: delete this client-side precheck entirely and let the server decide.
  const ourEnd = effectiveOccupancyExclEndIso(newMoveOutRaw) || "9999-12-31";
  const list = Array.isArray(unitTenancies) ? unitTenancies : [];
  for (const t of list) {
    if (String(t.room_id || t.roomId || "").trim() !== rid) continue;
    const st = String(t.status || "").trim().toLowerCase();
    if (st !== "active" && st !== "reserved") continue;
    const tIn = dateOnlyOrNull(t.move_in_date);
    if (!tIn) continue;
    // Existing side: stored (already-derived) move_out_date + 1.
    const tEnd = effectiveOccupancyExclEndIso(t.move_out_date) || "9999-12-31";
    if (ourStart < tEnd && ourEnd > tIn) return true;
  }
  return false;
}

export function assignRoomStatusSuffixDe(status) {
  if (status === "belegt") return "belegt";
  if (status === "reserviert") return "reserviert";
  return "frei";
}

export function assignRoomOptionTitleDe(status, disabled, overlaps) {
  if (disabled && overlaps && status === "frei") {
    return "Nicht verfügbar für gewählten Zeitraum (Überschneidung)";
  }
  if (status === "belegt") return "Bereits vermietet / belegt";
  if (status === "reserviert") return "Bereits reserviert";
  return "Verfügbar";
}

export function applyRecurringRevenueDatesFromTenancy(form, freqRaw, moveInRaw, _moveOutRaw) {
  const nf = normalizeRevenueFrequency(freqRaw);
  const mi = dateOnlyOrNull(moveInRaw) || "";
  if (nf === "one_time") {
    return { ...form, frequency: freqRaw, start_date: "", end_date: "" };
  }
  return {
    ...form,
    frequency: freqRaw,
    start_date: form.start_date || mi,
    end_date: "",
  };
}

/** Zeitraum column: recurring ends follow tenancy display end; one_time uses stored dates. */
export function revenueRowZeitraumDisplay(rr, tn) {
  const f = normalizeRevenueFrequency(rr?.frequency);
  const startIso =
    dateOnlyOrNull(rr?.start_date) || dateOnlyOrNull(tn?.move_in_date) || "";
  const startLabel = startIso ? String(startIso).slice(0, 10) : "—";
  if (f === "one_time") {
    const s = rr?.start_date ? String(rr.start_date).slice(0, 10) : "—";
    const e = rr?.end_date ? String(rr.end_date).slice(0, 10) : "—";
    return `${s} – ${e}`;
  }
  const endLabel = tenancyDisplayEndIso(tn) || "—";
  return `${startLabel} – ${endLabel}`;
}

export function deriveTenancyStatusFromDates(moveInRaw, moveOutRaw, todayIso = getTodayIsoForOccupancy()) {
  const today = String(todayIso || "").slice(0, 10);
  const mi = moveInRaw != null ? String(moveInRaw).slice(0, 10) : "";
  const mo = moveOutRaw != null ? String(moveOutRaw).slice(0, 10) : "";
  if (mo && mo < today) return "ended";
  if (mi && mi > today) return "reserved";
  if (mi) return "active";
  return "";
}

export function tenancyStatusLabelFromDerived(key) {
  if (key === "ended") return "Beendet";
  if (key === "reserved") return "Reserviert";
  if (key === "active") return "Aktiv";
  return "—";
}

export function tenancyDateRangeLabel(tn) {
  const mi = tn.move_in_date;
  const mo = tenancyDisplayEndIso(tn);
  if (mo == null || mo === "") {
    return `seit ${formatDateOnly(mi)}`;
  }
  return `${formatDateOnly(mi)} – ${formatDateOnly(mo)}`;
}

/** Short German line when API returns participants with co_tenant / solidarhafter. */
export function secondParticipantZweitmieterLine(participants) {
  if (!Array.isArray(participants) || participants.length === 0) return null;
  const sec = participants.find(
    (p) => p && (p.role === "co_tenant" || p.role === "solidarhafter")
  );
  if (!sec) return null;
  const name = tenantDisplayName(sec.tenant) || String(sec.tenant_id || "").trim();
  if (!name) return null;
  const roleDe = sec.role === "co_tenant" ? "Co-Mieter" : "Solidarhafter";
  return `Zweitmieter: ${name} (${roleDe})`;
}

/** When viewing tenant detail as co-tenant/solidarhafter (not tenancies.tenant_id). */
export function viewerTenancyRoleLabelDe(tenancy, viewerTenantId) {
  if (String(tenancy?.tenant_id) === String(viewerTenantId)) return null;
  const parts = tenancy?.participants;
  if (!Array.isArray(parts)) return null;
  const me = parts.find((p) => p && String(p.tenant_id) === String(viewerTenantId));
  if (!me?.role) return null;
  if (me.role === "co_tenant") return "Co-Mieter (Zweitmieter)";
  if (me.role === "solidarhafter") return "Solidarhafter";
  return null;
}

/**
 * True if `tenantId` is a party on `tenancy` — either the primary tenant
 * (`tenancy.tenant_id`) OR present in `tenancy.participants[]`. Supports both
 * participant shapes (`participant.tenant_id` and `participant.tenant?.id`).
 *
 * Used so a co-tenant (participant-only, not the primary `tenant_id`) is not
 * wrongly treated as having no tenancies — which made
 * `deriveTenantOperationalStatus` return "inactive" / show an "Inaktiv" badge.
 */
export function tenancyIncludesTenant(tenancy, tenantId) {
  if (!tenancy) return false;
  const tid = String(tenantId);
  if (String(tenancy.tenant_id) === tid) return true;
  const parts = tenancy.participants;
  if (!Array.isArray(parts)) return false;
  return parts.some(
    (p) =>
      p &&
      (String(p.tenant_id) === tid ||
        (p.tenant && String(p.tenant.id) === tid))
  );
}

/** Primary-tenant tenancies for this page: find active/reserved row for room/unit (PATCH vs POST). */
export function findOpenPrimaryTenancyForRoom(tenancyList, unitId, roomId) {
  const uid = String(unitId);
  const rid = String(roomId);
  const candidates = (tenancyList || []).filter(
    (t) => String(t.unit_id) === uid && String(t.room_id) === rid
  );
  const isOpen = (s) => {
    const x = String(s || "").toLowerCase();
    return x === "active" || x === "reserved";
  };
  const open = candidates.filter((t) => isOpen(t.status));
  if (!open.length) return null;
  open.sort((a, b) => {
    const da = String(a.move_in_date || "");
    const db = String(b.move_in_date || "");
    return db.localeCompare(da);
  });
  return open[0];
}

export function getStatusMeta(status) {
  const normalized = String(status || "").toLowerCase();
  if (
    normalized === "active" ||
    normalized === "aktiv" ||
    normalized === "belegt"
  ) {
    return {
      label: "Aktiv",
      bg: "#DCFCE7",
      color: "#166534",
      border: "#86EFAC",
    };
  }
  if (normalized === "reserved" || normalized === "reserviert") {
    return {
      label: "Reserviert",
      bg: "#FEF3C7",
      color: "#92400E",
      border: "#FCD34D",
    };
  }
  if (
    normalized === "ended" ||
    normalized === "beendet" ||
    normalized === "move_out" ||
    normalized === "ausgezogen"
  ) {
    return {
      label: "Ausgezogen",
      bg: "#E5E7EB",
      color: "#374151",
      border: "#D1D5DB",
    };
  }
  // Tenant-LEVEL only, and only reachable when every tenancy of this tenant was
  // cancelled (deriveTenantOperationalStatus). The admin term matches the
  // overview's; the tenancy-level `tenancyDisplayStatusLabelDe` and
  // `getTenancyLifecycleMeta` keep saying the neutral "Storniert", because one
  // cancelled contract is a cancelled contract — it is the summary over a
  // PERSON that has to say nobody ever moved in.
  if (normalized === "cancelled" || normalized === "storniert") {
    return {
      label: "Vor Einzug storniert",
      bg: "#FFE4E6",
      color: "#BE123C",
      border: "#FDA4AF",
    };
  }
  if (normalized === "inactive" || normalized === "inaktiv") {
    return {
      label: "Inaktiv",
      bg: "#F1F5F9",
      color: "#475569",
      border: "#CBD5E1",
    };
  }
  return {
    label: status || "Offen",
    bg: "#F1F5F9",
    color: "#475569",
    border: "#CBD5E1",
  };
}

export function tenantInitialsFromTenant(t) {
  if (!t) return "?";
  const fn = String(t.first_name || "").trim();
  const ln = String(t.last_name || "").trim();
  if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();
  const name = tenantDisplayName(t) || "";
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

export function initialAssignSecondForm() {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    postalCode: "",
    city: "",
    country: "CH",
    nationality: "",
    isSwiss: null,
    residencePermit: "",
  };
}

/**
 * Validate the second-person (co-tenant) selection on the assign form.
 *
 * Cluster A / A1: the second person can be either an EXISTING tenant (picked via
 * search — no new tenant row created) or a NEW tenant (inline form). This pure
 * helper centralizes the per-mode validation so the submit handler stays thin
 * and the "existing mode never creates a tenant" rule is unit-testable.
 *
 * Returns `{ ok: true, role }` or `{ ok: false, error }` (German message).
 */
export function validateSecondParticipant({
  mode,
  selectedTenantId,
  firstName,
  lastName,
  role,
}) {
  const r = String(role || "").trim();
  if (r !== "co_tenant" && r !== "solidarhafter") {
    return { ok: false, error: "Bitte Rolle wählen (Zweitmieter oder Solidarhafter)." };
  }
  if (mode === "existing") {
    if (!selectedTenantId) {
      return { ok: false, error: "Bitte einen bestehenden Mieter auswählen." };
    }
    return { ok: true, role: r };
  }
  const fn = String(firstName || "").trim();
  const ln = String(lastName || "").trim();
  if (!fn || !ln) {
    return { ok: false, error: "Bitte Vor- und Nachnamen für den Zweitmieter angeben." };
  }
  return { ok: true, role: r };
}

/**
 * Human-readable room/unit designation for a tenancy header title.
 * Prefers "<room_name> · <unit_short_unit_id>" (e.g. "Zimmer 2 · CL-001"),
 * falls back to either alone, then unit_title, then a generic label.
 * Null-tolerant: the room/unit display fields are populated on the list
 * endpoint but null on single-tenancy CRUD responses.
 */
export function tenancyUnitLabel(tn) {
  if (!tn) return "Mietverhältnis";
  const room = tn.room_name ? String(tn.room_name).trim() : "";
  const unit = tn.unit_short_unit_id ? String(tn.unit_short_unit_id).trim() : "";
  const primary = [room, unit].filter(Boolean).join(" · ");
  if (primary) return primary;
  const title = tn.unit_title ? String(tn.unit_title).trim() : "";
  if (title) return title;
  return "Mietverhältnis";
}

/**
 * Human-readable unit label for the inspection checklist header. Prefers the
 * unit title, then the short unit id, and NEVER exposes a raw unit UUID.
 * Returns the full "Einheit · <name>" string, or a bare "Einheit" when no
 * readable field is present. List-endpoint tenancies carry unit_title /
 * unit_short_unit_id; single-CRUD responses may not — a raw unit_id must never
 * leak into the UI, so it is deliberately not used as a fallback here.
 */
export function readableUnitHeaderLabel(tenancy) {
  if (!tenancy) return "Einheit";
  const title = tenancy.unit_title ? String(tenancy.unit_title).trim() : "";
  const shortId = tenancy.unit_short_unit_id
    ? String(tenancy.unit_short_unit_id).trim()
    : "";
  const name = title || shortId;
  return name ? `Einheit · ${name}` : "Einheit";
}
